import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const clearSession = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
    delete api.defaults.headers.common['Authorization'];
  }, []);

  const logout = useCallback(async () => {
    try {
      if (localStorage.getItem('token')) await api.post('/auth/logout', null, { timeout: 5000 });
    } catch {
      // El cierre local debe completarse aunque el token ya haya expirado.
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const fetchUser = useCallback(async () => {
    try {
      // Timeout de 10 segundos
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout: El servidor no respondió')), 10000)
      );
      
      const response = await Promise.race([
        api.get('/auth/me'),
        timeoutPromise
      ]);
      
      setUser(response.data);
    } catch (error) {
      clearSession();
    } finally {
      setLoading(false);
    }
  }, [clearSession]);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);


  const login = async (username, password, tenantCode = null) => {
    try {
      // Timeout de 15 segundos
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout: El servidor no respondió en 15 segundos')), 15000)
      );
      
      const loginPromise = api.post('/auth/login', {
        username,
        password,
        tenant_code: tenantCode || null,
      });
      
      const response = await Promise.race([loginPromise, timeoutPromise]);
      
      const { access_token, refresh_token, user: userData } = response.data;
      
      localStorage.removeItem('auth_notice');
      localStorage.setItem('token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      setToken(access_token);
      setUser(userData);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Error al iniciar sesión.',
      };
    }
  };


  const value = {
    user,
    loading,
    login,
    logout,
    fetchUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

